/**
 * SEO (`/seo`) — the SEO section's Overview surface (plans/seo quick
 * slice 1).
 *
 * The SEO score trend — the Lighthouse SEO category score from the
 * synthetic trend (`avg_seo_score` has landed on every audit since
 * day one; this is its first first-class surface). Deploy markers on
 * the baseline, same as the Performance charts.
 *
 * Sitemap health lives on the section's `/seo/sitemap` child; the GSC
 * Search lens (`/traffic/search`) stays separate until Google OAuth
 * verification clears, then joins this section as another child.
 */
import { TrendCurve, type TrendCurveSpec, type TrendLine } from '@/components/charts/TrendCurve';
import { SectionBoundary } from '@/components/primitives/SectionBoundary';
import { SiteIsland } from '@/components/shell/SiteIsland';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useActiveSiteUuid } from '@/lib/active-site';
import type { PageWithRecentAudits, TrendBucketRow } from '@/lib/api-types';
import { bandText, formatScore, scoreBand } from '@/lib/format';
import { useSiteDeploys, useSitePagesWithAudits, useSiteTrend } from '@/lib/queries';
import { Link, createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';

export const Route = createFileRoute('/_app/seo/')({
  component: SeoPage,
});

function SeoPage(): ReactElement {
  const siteUuid = useActiveSiteUuid();
  return (
    <SiteIsland section="SEO">
      {siteUuid && (
        <div className="space-y-4">
          <SectionBoundary label="the SEO score trend">
            <ScoreTrendCard siteUuid={siteUuid} />
          </SectionBoundary>
          <SectionBoundary label="pages by SEO score">
            <WorstPagesCard siteUuid={siteUuid} />
          </SectionBoundary>
        </div>
      )}
    </SiteIsland>
  );
}

// ─── SEO score trend ──────────────────────────────────────────────────────────

const RANGE_DAYS = 28;

/** Mirrors the Audits tab's Lab SEO spec (PerformanceSection's
 *  module-private `LAB_METRICS`) — small enough to duplicate rather
 *  than export from that 2,000-line module. */
const SEO_SPEC: TrendCurveSpec = {
  label: 'SEO score',
  unit: 'score',
  band: scoreBand,
  format: (v) => (v == null || !Number.isFinite(v) ? '—' : String(Math.round(v))),
  goodThreshold: 90,
  poorThreshold: 50,
};

/** Per-day average of `avg_seo_score` across the trend's (day, device-
 *  profile, region) tuples — the SEO-only sibling of the Audits tab's
 *  `buildLabLine`. Scores are stored 0–1; ×100 for the 90/50 axis. */
function buildSeoLine(rows: TrendBucketRow[] | undefined): TrendLine[] {
  if (!rows || rows.length === 0) return [];
  const byDay = new Map<string, { sum: number; n: number }>();
  for (const r of rows) {
    const v = r.avg_seo_score;
    if (v == null) continue;
    const day = r.bucket.slice(0, 10);
    const cur = byDay.get(day) ?? { sum: 0, n: 0 };
    cur.sum += v;
    cur.n += 1;
    byDay.set(day, cur);
  }
  const points = [...byDay.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([day, { sum, n }]) => ({ day, value: n > 0 ? (sum / n) * 100 : null }));
  return points.some((p) => p.value != null)
    ? [{ key: 'seo', color: 'var(--color-accent-brand)', points }]
    : [];
}

function ScoreTrendCard({ siteUuid }: { siteUuid: string }): ReactElement {
  const trend = useSiteTrend(siteUuid, { interval: 'day', range: RANGE_DAYS });
  const deploys = useSiteDeploys(siteUuid, RANGE_DAYS);
  const lines = buildSeoLine(trend.data);

  return (
    <section className="rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)]">
      <header className="flex items-center justify-between gap-2 border-b border-[var(--color-line-soft)] px-4 py-3">
        <h3 className="text-sm font-semibold text-[var(--color-ink)]">SEO score</h3>
        <span className="text-xs text-[var(--color-mute)]">
          Lighthouse SEO category · all audited pages · {RANGE_DAYS} days
        </span>
      </header>
      <div className="px-2 py-3">
        {trend.isLoading ? (
          <Skeleton className="h-48 w-full" />
        ) : lines.length === 0 ? (
          <p className="p-4 text-sm text-[var(--color-mute)]">
            No audits in this window yet — the SEO score lands with every Lighthouse run.
          </p>
        ) : (
          <TrendCurve
            lines={lines}
            spec={SEO_SPEC}
            deploys={deploys.data ?? []}
            previousPoints={[]}
          />
        )}
      </div>
    </section>
  );
}

// ─── Pages by SEO score ───────────────────────────────────────────────────────

interface PageScoreRow {
  uuid: string;
  name: string;
  path: string;
  /** Latest audited SEO score (0–100), or null when never audited. */
  score: number | null;
  /** Snapshot uuid of the audit the score came from. */
  auditId: string | null;
}

/** One row per monitored page with its newest audited SEO score, worst
 *  first — the "which pages dragged the trend down" answer. Picks the
 *  newest audit by timestamp rather than array position so it doesn't
 *  depend on the endpoint's ordering. */
function buildPageRows(pages: PageWithRecentAudits[] | undefined): PageScoreRow[] {
  if (!pages) return [];
  const rows = pages.map((p) => {
    let newest: { t: number; score: number; auditId: string } | null = null;
    for (const audit of p.recent_audits ?? []) {
      const v = audit.metrics.seo;
      if (typeof v !== 'number') continue;
      const t = Date.parse(audit.timestamp);
      // The pages endpoint returns the internal `ss-…` snapshot uuid;
      // the audit surface's public URLs use `aud_…` (same tail).
      const auditId = audit.snapshot_uuid.startsWith('ss-')
        ? `aud_${audit.snapshot_uuid.slice(3)}`
        : audit.snapshot_uuid;
      if (!newest || t > newest.t) newest = { t, score: v, auditId };
    }
    let path = p.url;
    try {
      path = new URL(p.url).pathname || '/';
    } catch {
      // Keep the raw URL when it doesn't parse.
    }
    return {
      uuid: p.uuid,
      name: p.name,
      path,
      score: newest?.score ?? null,
      auditId: newest?.auditId ?? null,
    };
  });
  return rows.sort((a, b) => (a.score ?? 101) - (b.score ?? 101));
}

function WorstPagesCard({ siteUuid }: { siteUuid: string }): ReactElement {
  const pages = useSitePagesWithAudits(siteUuid, ['seo']);
  const rows = buildPageRows(pages.data);

  return (
    <section className="rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)]">
      <header className="flex items-center justify-between gap-2 border-b border-[var(--color-line-soft)] px-4 py-3">
        <h3 className="text-sm font-semibold text-[var(--color-ink)]">Pages by SEO score</h3>
        <span className="text-xs text-[var(--color-mute)]">
          Latest audit per page · worst first
        </span>
      </header>
      {pages.isLoading ? (
        <div className="space-y-2 p-4">
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-4/5" />
        </div>
      ) : rows.length === 0 ? (
        <p className="p-4 text-sm text-[var(--color-mute)]">
          No monitored pages yet — add pages to see their SEO scores here.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Page</TableHead>
              <TableHead className="text-right">SEO score</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.uuid}>
                <TableCell className="max-w-96 truncate font-medium" title={row.path}>
                  {row.auditId ? (
                    <Link
                      to="/performance/audits/$auditId"
                      params={{ auditId: row.auditId }}
                      className="hover:underline"
                    >
                      {row.name} <span className="text-[var(--color-mute)]">{row.path}</span>
                    </Link>
                  ) : (
                    <>
                      {row.name} <span className="text-[var(--color-mute)]">{row.path}</span>
                    </>
                  )}
                </TableCell>
                <TableCell
                  className={`text-right font-semibold tabular-nums ${bandText(scoreBand(row.score))}`}
                >
                  {formatScore(row.score)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  );
}
